import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionEvidence, OptiBayEvidenceType } from '@/types/optibay';

const sb = supabase as any;

export function useSessionEvidence(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-evidence', sessionId],
    queryFn: async (): Promise<OptiBaySessionEvidence[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_evidence')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionEvidence[];
    },
    enabled: !!sessionId,
  });
}

export function useTestPathEvidence(sessionTestPathId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-test-path-evidence', sessionTestPathId],
    queryFn: async (): Promise<OptiBaySessionEvidence[]> => {
      if (!sessionTestPathId) return [];
      const { data, error } = await sb
        .from('optibay_session_evidence')
        .select('*')
        .eq('session_test_path_id', sessionTestPathId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionEvidence[];
    },
    enabled: !!sessionTestPathId,
  });
}

export interface AddEvidenceParams {
  sessionId: string;
  sessionTestPathId: string;
  evidenceType: OptiBayEvidenceType;
  artifactId?: string;
  valueNumeric?: number;
  valueBoolean?: boolean;
  valueText?: string;
  label?: string;
  unit?: string;
}

export function useAddEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddEvidenceParams) => {
      const { data, error } = await sb
        .from('optibay_session_evidence')
        .insert({
          session_id: params.sessionId,
          session_test_path_id: params.sessionTestPathId,
          evidence_type: params.evidenceType,
          artifact_id: params.artifactId || null,
          value_numeric: params.valueNumeric ?? null,
          value_boolean: params.valueBoolean ?? null,
          value_text: params.valueText || null,
          label: params.label || null,
          unit: params.unit || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionEvidence;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-evidence', data.session_id] });
      qc.invalidateQueries({ queryKey: ['optibay-test-path-evidence', data.session_test_path_id] });
    },
  });
}

export function useRemoveEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId, sessionTestPathId }: {
      id: string;
      sessionId: string;
      sessionTestPathId: string;
    }) => {
      const { error } = await sb
        .from('optibay_session_evidence')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId, sessionTestPathId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-evidence', result.sessionId] });
      qc.invalidateQueries({ queryKey: ['optibay-test-path-evidence', result.sessionTestPathId] });
    },
  });
}
